'use client';

import React, { useEffect } from 'react';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { getLocale } from '@/i18n/locale';
import { ELocale } from '@/i18n/localeEnum';
import { SectionCard } from '@/components/ui/section-card';

export const getWeskiPartnerId = () => {
  const locale = getLocale();
  switch (locale) {
    case ELocale.EN_US:
    case ELocale.EN_GB:
    case ELocale.CS_CZ:
    case ELocale.SK_SK:
      return '3e08';
    case ELocale.FR_FR:
      return 'yc8q';
    default:
      // for all other locales
      return 'p7s9';
  }
};

const IS_FR = getLocale() === ELocale.FR_FR;

const WESKI_SCRIPT_SRC = 'https://widget.weski.com';
const WESKI_SCRIPT_SELECTOR = `script[src="${WESKI_SCRIPT_SRC}"]`;

// How many WeSkiWidget instances are currently mounted. The vendor script is a
// page-level singleton that scans the whole document, so it is loaded once for
// the first instance rather than once per instance — several widgets can share
// one execution (e.g. the sidebar widget plus the mobile bottom popup).
let mountedWidgets = 0;

// Undoes the global monkeypatches below. Held at module scope so the last
// instance to unmount can restore the natives the first instance replaced.
let unpatchGlobals: (() => void) | null = null;

// Shadow roots our patched attachShadow has already created, keyed by host.
// A WeakMap so detached hosts from previous navigations are collectable.
const shadowRootsByHost = new WeakMap<Element, ShadowRoot>();

const removeWeskiScripts = () => {
  document.querySelectorAll(WESKI_SCRIPT_SELECTOR).forEach((script) => script.remove());
};

// Patch the two global APIs the WeSki script uses to install itself, so that
// executing it more than once per page session is a no-op instead of a throw.
// Both natives reject a second call for the same target, and WeSki does not
// catch: one `NotSupportedError` aborts the rest of its init and we get a blank
// widget. Returns the function that restores the natives.
const patchGlobals = () => {
  const originalAttachShadow = Element.prototype.attachShadow;
  const customElementsRegistry = window.customElements;
  const originalDefine = customElementsRegistry?.define;

  Element.prototype.attachShadow = function attachShadow(init) {
    // WeSki uses a closed shadow root (mode: 'closed'), so element.shadowRoot
    // is always null from the outside and we cannot poll for it. Intercepting
    // the native method is the only reliable way to know the widget has
    // mounted. When running inside the mobile app's WebView, notify the native
    // side so it can reveal the previously hidden WebView container.
    if ((window as any).ReactNativeWebView) {
      (window as any).ReactNativeWebView.postMessage(JSON.stringify({ event: 'weski_loaded' }));
    }

    // A host WeSki has already initialised keeps the root it was given rather
    // than throwing, which is what lets the script run again on the next
    // navigation.
    const existing = shadowRootsByHost.get(this);
    if (existing) {
      return existing;
    }

    const shadowRoot = originalAttachShadow.call(this, init);
    shadowRootsByHost.set(this, shadowRoot);
    return shadowRoot;
  };

  if (customElementsRegistry && originalDefine) {
    customElementsRegistry.define = function define(name, constructor, options) {
      // Custom element names are global and permanent; re-registering one
      // throws. The definition from the first execution is still live and does
      // the same job, so skip it.
      if (customElementsRegistry.get(name)) {
        return;
      }
      originalDefine.call(customElementsRegistry, name, constructor, options);
    };
  }

  return () => {
    Element.prototype.attachShadow = originalAttachShadow;
    if (customElementsRegistry && originalDefine) {
      customElementsRegistry.define = originalDefine;
    }
  };
};

const WeSkiWidget = ({ destinationId, withTopCopy }: { destinationId: string, withTopCopy?: boolean }) => {
  const { t } = useOTSTranslation('common');

  useEffect(() => {
    mountedWidgets += 1;

    // Only the first mounted instance owns the script and the patches; any
    // sibling widget in the same commit is picked up by the same execution.
    if (mountedWidgets === 1) {
      unpatchGlobals = patchGlobals();

      // The WeSki script scans the document for `#weski-widget` when it runs,
      // and offers no API to re-scan. Under the App Router a client-side
      // navigation replaces that element with a brand new node, which the
      // already-executed script never sees — so the widget rendered only on
      // the page that happened to be loaded from the server (OWR-2401).
      // Dropping the old tag and appending a fresh one re-executes the script
      // against the current DOM; patchGlobals makes that re-run safe.
      removeWeskiScripts();

      const script = document.createElement('script');
      script.src = WESKI_SCRIPT_SRC;
      script.async = true;
      document.body.appendChild(script);
    }

    return () => {
      mountedWidgets -= 1;

      // Last one out: drop the tag and restore the natives, so an unrelated
      // page is not left with our patches and the next mount starts clean.
      if (mountedWidgets === 0) {
        removeWeskiScripts();
        unpatchGlobals?.();
        unpatchGlobals = null;
      }
    };
  }, []);

  return (
    <SectionCard as="article" padding="none" className="overflow-hidden">
      {withTopCopy
        && (
          <p className="p-4">
            {t('resort.bookTrip.copy')}
          </p>
        )}
      <div
        id="weski-widget"
        data-destination-id={destinationId}
        data-locale={IS_FR ? 'fr' : undefined}
        data-partner={getWeskiPartnerId()}
        data-new-tab="true"
      />
    </SectionCard>
  );
};

export default WeSkiWidget;
